import { createApi } from '@reduxjs/toolkit/query/react';

import { ICustomer, ICustomerParams, TCreateCustomerBillingSettings, TCustomerBillingSettings } from '@/types/customers';

import { baseQueryWithReauth } from './utils';

export const customersApi = createApi({
  reducerPath: 'customersApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getCustomers: build.query({
      query: (params: { page: number; limit: number; search?: string; }) => {
        return {
          url: 'customer/get-customers',
          method: 'POST',
          body: { ...params, search: params.search || '' },
        };
      },
    }),
    getCustomer: build.query({
      query: (params: { referenceId: string }) => {
        return {
          url: 'customer',
          method: 'GET',
          params,
        };
      },
    }),
    createCustomer: build.mutation({
      query: (customer: ICustomerParams) => {
        return {
          url: 'customer',
          method: 'POST',
          body: customer,
        };
      },
    }),
    deleteCustomer: build.mutation({
      query: (referenceId: string) => {
        return {
          url: 'customer',
          params: {
            referenceId,
          },
          method: 'DELETE',
          responseHandler: (response) => response.text(),
        };
      },
    }),
    updateCustomer: build.mutation({
      query: (customer: ICustomer) => {
        return {
          url: 'customer',
          method: 'PATCH',
          body: customer,
        };
      },
    }),
    getCustomerByUser: build.query({
      query: () => {
        return {
          url: 'customer/get-by-user',
          method: 'GET',
        };
      },
    }),
    getCustomerBillingSettings: build.query({
      query: (customerId: string) => {
        return {
          url: 'customer/get-customer-billing-settings',
          method: 'GET',
          params: { customerId },
        };
      },
    }),
    createCustomerBillingSettings: build.mutation({
      query: (customerBillingSettings: TCreateCustomerBillingSettings) => {
        return {
          url: 'customer/create-billing-settings',
          method: 'POST',
          body: customerBillingSettings,
        };
      },
      transformResponse: (response: TCustomerBillingSettings) => response,
    }),
    uploadFile: build.mutation({
      query: (params: { customerId?: string; file: File }) => {
        const { customerId, file } = params;

        const formData = new FormData();
        formData.append('file', file);

        return {
          url: 'customer/upload-file',
          method: 'POST',
          ...(customerId && { params: { customerId } }),
          body: formData,
          responseHandler: (response) => response.text(),
        };
      },
    }),
  }),
});

export const {
  useGetCustomerQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useLazyGetCustomerQuery,
  useCreateCustomerMutation,
  useDeleteCustomerMutation,
  useUpdateCustomerMutation,
  useGetCustomerByUserQuery,
  useGetCustomerBillingSettingsQuery,
  useCreateCustomerBillingSettingsMutation,
  useUploadFileMutation,
} = customersApi;
